<template>
  <div class="fluxo-editor-pagina">
    <div class="fluxo-editor-toolbar d-flex align-items-center justify-content-between px-1 py-50">
      <div>
        <router-link to="/fluxos" class="btn btn-sm btn-outline-secondary mr-1">
          <i class="fa fa-arrow-left"></i> Voltar
        </router-link>
        <strong>{{ fluxo.nome }}</strong>
        <span class="text-muted ml-1" v-if="salvandoTexto">{{ salvandoTexto }}</span>
      </div>
      <div>
        <button class="btn btn-sm btn-outline-secondary mr-50" :disabled="indiceHistorico <= 0" @click="desfazer">
          <i class="fa fa-undo"></i> Desfazer
        </button>
        <button class="btn btn-sm btn-outline-secondary mr-50" :disabled="indiceHistorico >= historico.length - 1" @click="refazer">
          <i class="fa fa-redo"></i> Refazer
        </button>
        <button
          v-if="problemasValidacao.length"
          class="btn btn-sm btn-outline-warning mr-50"
          @click="mostrarProblemas = !mostrarProblemas"
        >
          <i class="fa fa-exclamation-triangle"></i> {{ problemasValidacao.length }} pendência(s)
        </button>
        <button class="btn btn-sm btn-outline-secondary mr-50" @click="ajustarTela">
          <i class="fa fa-expand"></i> Ajustar à tela
        </button>
        <button class="btn btn-sm btn-outline-info mr-50" @click="simulando = true">
          <i class="fa fa-comments"></i> Simular
        </button>
        <button class="btn btn-sm btn-outline-primary mr-50" @click="salvarRascunho">
          Salvar rascunho
        </button>
        <button class="btn btn-sm btn-success" @click="publicar">
          Publicar
        </button>
      </div>
    </div>

    <div class="fluxo-editor-corpo">
      <div class="fluxo-editor-paleta">
        <h6 class="px-1 pt-1">Nós (clique pra adicionar)</h6>
        <div
          v-for="no in catalogoNos.filter(n => n.tipo !== 'nota' || true)"
          :key="no.tipo"
          class="fluxo-paleta-item"
          @click="adicionarNo(no.tipo)"
        >
          <strong>{{ no.rotulo }}</strong>
          <small class="d-block text-muted">{{ no.descricao }}</small>
        </div>
      </div>

      <div ref="area" class="fluxo-editor-area">
        <!-- Sem class de proposito: o Drawflow decide o clique pela PRIMEIRA
             classe do elemento (classList[0]) e so reconhece "parent-drawflow",
             que ele mesmo adiciona. Com outra classe na frente, clicar no
             fundo fora do desenho (aparece com zoom reduzido ou depois de
             mover a tela) nao arrastava o canvas. Estilo vai pelo id. -->
        <div id="fluxo-drawflow" ref="drawflow"></div>

        <!-- Overlays por cima do canvas, e nao irmaos no flex: se mudassem o
             tamanho do canvas no meio de um clique (inspetor abre no
             nodeSelected), o Drawflow com zoom != 1 desloca o desenho debaixo
             do mouse e o arraste "escapa". -->
        <div v-if="problemasValidacao.length && mostrarProblemas" class="fluxo-editor-problemas alert alert-warning mb-0 py-50">
          <div class="d-flex justify-content-between align-items-center">
            <strong>Antes de publicar, resolva:</strong>
            <button class="btn btn-sm btn-link p-0" title="Minimizar" @click="mostrarProblemas = false">
              <i class="fa fa-minus"></i>
            </button>
          </div>
          <ul class="mb-0">
            <li
              v-for="(p, i) in problemasValidacao"
              :key="i"
              :class="{ 'fluxo-problema-link': p.noId }"
              :title="p.noId ? 'Ir para o nó' : ''"
              @click="p.noId && irParaNo(p.noId)"
            >
              {{ p.texto }}
            </li>
          </ul>
        </div>

        <!-- Botao no meio da linha selecionada (Delete no teclado tambem
             remove). Fica fora do container do Drawflow pra o clique nao
             desselecionar a linha antes de remover. -->
        <button
          v-if="conexaoSelecionada && posicaoBotaoConexao"
          class="btn btn-sm btn-danger fluxo-remover-conexao"
          :style="{ left: posicaoBotaoConexao.left + 'px', top: posicaoBotaoConexao.top + 'px' }"
          title="Remover ligação (ou tecle Delete)"
          @click="removerConexaoSelecionada"
        >
          <i class="fa fa-trash"></i> Remover ligação
        </button>

        <div v-if="noSelecionado" class="fluxo-editor-inspetor">
          <inspetor-no
            :no="noSelecionado"
            :catalogo="catalogoNos"
            :departamentos="departamentos"
            :fluxo-id="fluxo.id"
            @atualizar="atualizarConteudoNo"
            @remover="removerNoSelecionado"
            @fechar="fecharInspetor"
          />
        </div>
      </div>
    </div>

    <simulador
      v-if="simulando"
      :definicao="fluxo.definicao"
      :departamentos="departamentos"
      @fechar="simulando = false"
    />
  </div>
</template>

<script>
import Drawflow from 'drawflow'
import 'drawflow/dist/drawflow.min.css'
import InspetorNo from './components/InspetorNo.vue'
import Simulador from './components/Simulador.vue'
import { saidasDoNo, novoConteudoPadrao, validarFluxo } from '@/utils/fluxoSchema'

export default {
  components: { InspetorNo, Simulador },
  data() {
    return {
      fluxo: {},
      catalogoNos: [],
      departamentos: [],
      simulando: false,
      mostrarProblemas: true,
      mapaIdParaDrawflow: {},
      mapaDrawflowParaId: {},
      noSelecionadoId: null,
      conexaoSelecionada: null,
      posicaoBotaoConexao: null,
      historico: [],
      indiceHistorico: -1,
      timerHistorico: null,
      problemasValidacao: [],
      salvandoTexto: '',
      contador: 0,
    }
  },
  computed: {
    noSelecionado() {
      if (!this.noSelecionadoId) return null
      return (this.fluxo.definicao.nos || []).find(n => n.id === this.noSelecionadoId) || null
    },
  },
  created() {
    // Fora do data() de proposito: reativo, o Vue instrumenta todo o estado
    // interno do Drawflow e cada mousemove do arraste passa por setters
    // reativos - com fluxo grande o canvas engasga.
    this.editor = null
  },
  mounted() {
    this.carregar()
    window.addEventListener('mouseup', this.soltarForaDoCanvas)
  },
  beforeDestroy() {
    clearTimeout(this.timerHistorico)
    window.removeEventListener('mouseup', this.soltarForaDoCanvas)
  },
  methods: {
    async carregar() {
      const idFluxo = this.$route.params.id
      const apiBase = this.$store.state.api

      const [respFluxo, respCatalogo, respDeps] = await Promise.all([
        window.axios.get(apiBase + 'fluxos/' + idFluxo),
        window.axios.get(apiBase + 'fluxos/catalogo-nos'),
        window.axios.get(apiBase + 'departamentos', { params: { paginated: false } }),
      ])

      this.fluxo = respFluxo.data.data[0]
      if (!this.fluxo.definicao || !this.fluxo.definicao.nos) {
        this.fluxo.definicao = { versao_schema: 1, no_inicial: null, timeout_segundos: 900, mensagem_fallback: 'Não entendi. Vamos tentar de novo.', nos: [] }
      }
      this.catalogoNos = respCatalogo.data
      this.departamentos = respDeps.data.data

      this.contador = (this.fluxo.definicao.nos || []).reduce((max, no) => {
        const numero = parseInt(String(no.id).replace(/\D/g, ''), 10)
        return isNaN(numero) ? max : Math.max(max, numero)
      }, 0)

      this.$nextTick(() => {
        this.montarCanvas()
        this.registrarHistorico(true)
        this.validar()
      })
    },

    montarCanvas() {
      this.editor = new Drawflow(this.$refs.drawflow)
      this.editor.reroute = true
      this.editor.zoom_min = 0.2
      this.editor.start()
      this.importarDefinicao()
      this.ajustarTela()

      // Arrastar texto/imagem de dentro de um no inicia o drag nativo do
      // navegador, que engole os mousemove e o no para de acompanhar.
      this.$refs.drawflow.addEventListener('dragstart', e => e.preventDefault())
      this.$refs.drawflow.addEventListener('wheel', this.rolarCanvas, { passive: false })

      this.editor.on('nodeSelected', id => {
        this.noSelecionadoId = this.mapaDrawflowParaId[id] || null
      })
      this.editor.on('nodeUnselected', () => {
        this.noSelecionadoId = null
      })
      this.editor.on('nodeMoved', () => {
        this.sincronizarPosicoes()
        this.registrarHistorico()
      })
      // Delete no teclado e o "x" do botao direito removem direto no
      // Drawflow - sem isso o no sumia do canvas mas ficava na definicao.
      this.editor.on('nodeRemoved', idDrawflow => {
        this.removerNoDaDefinicao(this.mapaDrawflowParaId[idDrawflow])
      })
      this.editor.on('connectionCreated', dados => {
        this.sincronizarConexao(dados, true)
        this.registrarHistorico()
        this.validar()
      })
      this.editor.on('connectionSelected', dados => {
        this.conexaoSelecionada = dados
        this.posicionarBotaoConexao()
      })
      this.editor.on('connectionUnselected', () => {
        this.conexaoSelecionada = null
      })
      this.editor.on('translate', () => this.posicionarBotaoConexao())
      this.editor.on('zoom', () => this.$nextTick(this.posicionarBotaoConexao))
      this.editor.on('connectionRemoved', dados => {
        this.conexaoSelecionada = null
        this.sincronizarConexao(dados, false)
        this.registrarHistorico()
        this.validar()
      })
    },

    importarDefinicao() {
      this.editor.clear()
      this.conexaoSelecionada = null
      this.mapaIdParaDrawflow = {}
      this.mapaDrawflowParaId = {}

      const nos = this.fluxo.definicao.nos || []

      nos.forEach(no => {
        const numEntradas = no.tipo === 'inicio' ? 0 : 1
        const saidasEsperadas = saidasDoNo(no)
        const numSaidas = saidasEsperadas.length
        const html = this.gerarHtmlNo(no)
        const posX = (no.posicao && no.posicao.x) || 50
        const posY = (no.posicao && no.posicao.y) || 50

        const idDrawflow = this.editor.addNode(
          no.tipo, numEntradas, numSaidas, posX, posY,
          'fluxo-no fluxo-no-' + no.tipo, { meuId: no.id }, html, false
        )

        this.mapaIdParaDrawflow[no.id] = idDrawflow
        this.mapaDrawflowParaId[idDrawflow] = no.id
      })

      nos.forEach(no => {
        const saidasEsperadas = saidasDoNo(no)
        saidasEsperadas.forEach((saidaEsperada, indice) => {
          const saidaReal = (no.saidas || []).find(s => s.chave === saidaEsperada.chave)
          if (saidaReal && saidaReal.destino && this.mapaIdParaDrawflow[saidaReal.destino]) {
            this.editor.addConnection(
              this.mapaIdParaDrawflow[no.id],
              this.mapaIdParaDrawflow[saidaReal.destino],
              'output_' + (indice + 1),
              'input_1'
            )
          }
        })
      })
    },

    gerarHtmlNo(no) {
      const info = this.catalogoNos.find(c => c.tipo === no.tipo) || {}
      const conteudo = no.conteudo || {}
      let resumo = ''
      if (conteudo.texto) resumo = String(conteudo.texto).substring(0, 40)
      else if (no.tipo === 'transferir' || no.tipo === 'horario') {
        const dep = this.departamentos.find(d => d.id === conteudo.departamento_id)
        resumo = dep ? dep.nome : '(sem departamento)'
      } else if (no.tipo === 'condicao') resumo = conteudo.variavel || '(sem variável)'
      else if (no.tipo === 'aguardar') resumo = (conteudo.segundos || 0) + 's'
      else if (no.tipo === 'imagem') resumo = conteudo.legenda || conteudo.nome_arquivo || '(sem imagem)'
      else if (no.tipo === 'chamado_glpi') resumo = (Number(conteudo.tipo) === 2 ? 'Requisição' : 'Incidente') + (conteudo.categoria_nome ? ' - ' + conteudo.categoria_nome : '') + (['solucionado', 'fechado'].includes(conteudo.abrir_como) ? ' (' + conteudo.abrir_como + ')' : '')

      return '<div class="fluxo-no-titulo">' + (info.rotulo || no.tipo) + '</div>' +
        (resumo ? '<div class="fluxo-no-resumo">' + this.escapar(resumo) + '</div>' : '')
    },

    escapar(texto) {
      const div = document.createElement('div')
      div.innerText = texto
      return div.innerHTML
    },

    adicionarNo(tipo) {
      if (tipo === 'inicio' && this.fluxo.definicao.nos.some(n => n.tipo === 'inicio')) {
        this.$message(null, 'O fluxo já tem um nó de Início', 'error')
        return
      }

      this.contador++
      const id = 'no-' + this.contador
      const conteudo = novoConteudoPadrao(tipo)
      const no = {
        id,
        tipo,
        posicao: { x: 100 + (this.contador % 5) * 40, y: 100 + (this.contador % 7) * 60 },
        conteudo,
        saidas: saidasDoNo({ tipo, conteudo }).map(s => ({ chave: s.chave, rotulo: s.rotulo, destino: null })),
      }
      this.fluxo.definicao.nos.push(no)

      const numEntradas = tipo === 'inicio' ? 0 : 1
      const idDrawflow = this.editor.addNode(
        tipo, numEntradas, saidasDoNo(no).length, no.posicao.x, no.posicao.y,
        'fluxo-no fluxo-no-' + tipo, { meuId: id }, this.gerarHtmlNo(no), false
      )
      this.mapaIdParaDrawflow[id] = idDrawflow
      this.mapaDrawflowParaId[idDrawflow] = id

      this.registrarHistorico()
      this.validar()
    },

    atualizarConteudoNo(novoConteudo) {
      const no = this.noSelecionado
      if (!no) return

      const saidasAntes = saidasDoNo(no)
      no.conteudo = novoConteudo
      const saidasDepois = saidasDoNo(no)

      const saidasAntigas = no.saidas || []
      no.saidas = saidasDepois.map(s => {
        const existente = saidasAntigas.find(a => a.chave === s.chave)
        return { chave: s.chave, rotulo: s.rotulo, destino: existente ? existente.destino : null }
      })

      const idDrawflow = this.mapaIdParaDrawflow[no.id]
      const diferenca = saidasDepois.length - saidasAntes.length
      if (diferenca > 0) {
        for (let i = 0; i < diferenca; i++) this.editor.addNodeOutput(idDrawflow)
      } else if (diferenca < 0) {
        for (let i = 0; i < -diferenca; i++) {
          this.editor.removeNodeOutput(idDrawflow, 'output_' + (saidasAntes.length - i))
        }
      }

      this.editor.updateNodeDataFromId(idDrawflow, { meuId: no.id })
      const elementoConteudo = document.querySelector('#node-' + idDrawflow + ' .drawflow_content_node')
      if (elementoConteudo) elementoConteudo.innerHTML = this.gerarHtmlNo(no)

      this.registrarHistorico()
      this.validar()
    },

    // A limpeza da definicao acontece no evento nodeRemoved
    // (removerNoDaDefinicao), igual quando o no e apagado pelo teclado.
    removerNoSelecionado() {
      const no = this.noSelecionado
      if (!no) return
      this.editor.removeNodeId('node-' + this.mapaIdParaDrawflow[no.id])
    },

    removerNoDaDefinicao(meuId) {
      if (!meuId) return
      const idDrawflow = this.mapaIdParaDrawflow[meuId]

      this.fluxo.definicao.nos = this.fluxo.definicao.nos.filter(n => n.id !== meuId)
      this.fluxo.definicao.nos.forEach(outro => {
        (outro.saidas || []).forEach(s => {
          if (s.destino === meuId) s.destino = null
        })
      })
      if (this.fluxo.definicao.no_inicial === meuId) this.fluxo.definicao.no_inicial = null

      delete this.mapaDrawflowParaId[idDrawflow]
      delete this.mapaIdParaDrawflow[meuId]
      if (this.noSelecionadoId === meuId) this.noSelecionadoId = null

      this.registrarHistorico()
      this.validar()
    },

    fecharInspetor() {
      this.noSelecionadoId = null
    },

    sincronizarPosicoes() {
      const dados = this.editor.drawflow.drawflow[this.editor.module].data
      this.fluxo.definicao.nos.forEach(no => {
        const noDrawflow = dados[this.mapaIdParaDrawflow[no.id]]
        if (!noDrawflow) return
        const x = Math.round(noDrawflow.pos_x) || 0
        const y = Math.round(noDrawflow.pos_y) || 0
        if (!no.posicao || no.posicao.x !== x || no.posicao.y !== y) no.posicao = { x, y }
      })
    },

    // O Drawflow so escuta mouseup dentro do proprio canvas: soltar o botao
    // em cima da paleta/inspetor/toolbar deixava o arraste "preso" (o no
    // seguia o mouse ate o proximo clique).
    soltarForaDoCanvas(e) {
      if (this.editor && this.$refs.drawflow && !this.$refs.drawflow.contains(e.target)) {
        this.editor.dragEnd(e)
      }
    },

    // Roda do mouse/touchpad move o canvas; Ctrl+roda continua sendo o zoom
    // nativo do Drawflow.
    rolarCanvas(e) {
      if (e.ctrlKey || !this.editor) return
      e.preventDefault()
      const deltaX = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX
      const deltaY = e.shiftKey && !e.deltaX ? 0 : e.deltaY
      this.aplicarTransformacao(this.editor.canvas_x - deltaX, this.editor.canvas_y - deltaY, this.editor.zoom)
    },

    aplicarTransformacao(x, y, zoom) {
      this.editor.canvas_x = x
      this.editor.canvas_y = y
      this.editor.zoom = zoom
      this.editor.zoom_last_value = zoom
      this.editor.precanvas.style.transform = 'translate(' + x + 'px, ' + y + 'px) scale(' + zoom + ')'
      this.posicionarBotaoConexao()
    },

    // Meio do trecho clicado da linha, convertido pra coordenada da area -
    // recalculado a cada pan/zoom pra o botao acompanhar a linha.
    posicionarBotaoConexao() {
      if (!this.conexaoSelecionada) return
      const caminho = this.editor && this.editor.connection_selected
      if (!caminho || !caminho.isConnected) {
        this.conexaoSelecionada = null
        return
      }
      const ponto = caminho.getPointAtLength(caminho.getTotalLength() / 2).matrixTransform(caminho.getScreenCTM())
      const area = this.$refs.area.getBoundingClientRect()
      this.posicaoBotaoConexao = { left: ponto.x - area.left, top: ponto.y - area.top }
    },

    removerConexaoSelecionada() {
      if (this.editor.connection_selected) this.editor.removeConnection()
      this.conexaoSelecionada = null
    },

    sincronizarConexao(dados, criada) {
      const origemId = this.mapaDrawflowParaId[dados.output_id]
      const destinoId = this.mapaDrawflowParaId[dados.input_id]
      const no = (this.fluxo.definicao.nos || []).find(n => n.id === origemId)
      if (!no) return

      const indiceSaida = parseInt(String(dados.output_class).replace('output_', ''), 10) - 1
      const saidasEsperadas = saidasDoNo(no)
      const saidaEsperada = saidasEsperadas[indiceSaida]
      if (!saidaEsperada) return

      let saida = (no.saidas || []).find(s => s.chave === saidaEsperada.chave)
      if (!saida) {
        saida = { chave: saidaEsperada.chave, rotulo: saidaEsperada.rotulo, destino: null }
        no.saidas.push(saida)
      }
      saida.destino = criada ? destinoId : null
    },

    registrarHistorico(imediato) {
      clearTimeout(this.timerHistorico)
      const gravar = () => {
        const instantaneo = JSON.stringify(this.fluxo.definicao)
        this.historico = this.historico.slice(0, this.indiceHistorico + 1)
        this.historico.push(instantaneo)
        this.indiceHistorico = this.historico.length - 1
      }
      if (imediato) gravar()
      else this.timerHistorico = setTimeout(gravar, 400)
    },

    restaurarHistorico(indice) {
      this.indiceHistorico = indice
      this.fluxo.definicao = JSON.parse(this.historico[indice])
      this.noSelecionadoId = null
      this.$nextTick(() => {
        this.importarDefinicao()
        this.validar()
      })
    },

    desfazer() {
      if (this.indiceHistorico > 0) this.restaurarHistorico(this.indiceHistorico - 1)
    },

    refazer() {
      if (this.indiceHistorico < this.historico.length - 1) this.restaurarHistorico(this.indiceHistorico + 1)
    },

    // Enquadra todos os nos na area visivel. O precanvas do Drawflow escala
    // a partir do centro (transform-origin padrao), por isso o translate e
    // (centro da tela - centro do desenho) * zoom.
    ajustarTela() {
      const elementos = this.editor.precanvas.querySelectorAll('.drawflow-node')
      if (!elementos.length) {
        this.aplicarTransformacao(0, 0, 1)
        return
      }

      let minX = Infinity
      let minY = Infinity
      let maxX = -Infinity
      let maxY = -Infinity
      elementos.forEach(el => {
        minX = Math.min(minX, el.offsetLeft)
        minY = Math.min(minY, el.offsetTop)
        maxX = Math.max(maxX, el.offsetLeft + el.offsetWidth)
        maxY = Math.max(maxY, el.offsetTop + el.offsetHeight)
      })

      const margem = 40
      const largura = this.editor.precanvas.clientWidth
      const altura = this.editor.precanvas.clientHeight
      const zoomIdeal = Math.min((largura - margem * 2) / (maxX - minX), (altura - margem * 2) / (maxY - minY))
      const zoom = Math.max(this.editor.zoom_min, Math.min(1, zoomIdeal))

      this.aplicarTransformacao(
        (largura / 2 - (minX + maxX) / 2) * zoom,
        (altura / 2 - (minY + maxY) / 2) * zoom,
        zoom,
      )
    },

    validar() {
      this.sincronizarNoInicial()
      this.problemasValidacao = validarFluxo(this.fluxo.definicao)
      this.destacarPendentes()
    },

    // O ponto de partida e sempre o no do tipo "inicio". Antes era "o
    // primeiro no adicionado com no_inicial vazio": apagar o Inicio e criar
    // outro no antes de recria-lo fazia esse outro no virar o inicio. Fluxo
    // antigo sem no "inicio" mantem o no_inicial que ja tinha.
    sincronizarNoInicial() {
      const inicio = this.fluxo.definicao.nos.find(n => n.tipo === 'inicio')
      if (inicio) this.fluxo.definicao.no_inicial = inicio.id
    },

    // Pinta no canvas os nos que tem pendencia e poe a lista no tooltip.
    // Roda a cada validar(), entao sempre limpa antes de marcar de novo.
    destacarPendentes() {
      if (!this.editor) return
      const porNo = {}
      this.problemasValidacao.forEach(p => {
        if (p.noId) (porNo[p.noId] = porNo[p.noId] || []).push(p.texto)
      })

      this.editor.precanvas.querySelectorAll('.drawflow-node').forEach(el => {
        const meuId = this.mapaDrawflowParaId[el.id.slice('node-'.length)]
        const problemas = porNo[meuId]
        el.classList.toggle('fluxo-no-pendente', !!problemas)
        if (problemas) {
          el.setAttribute('data-pendencias', problemas.length)
          el.setAttribute('title', problemas.join('\n'))
        } else {
          el.removeAttribute('data-pendencias')
          el.removeAttribute('title')
        }
      })
    },

    // Centraliza o no na tela (mantendo o zoom) e seleciona ele, abrindo o
    // inspetor. Espelha o estado interno de selecao do Drawflow pra que
    // Delete/arraste continuem agindo sobre o mesmo no.
    irParaNo(meuId) {
      const el = document.getElementById('node-' + this.mapaIdParaDrawflow[meuId])
      if (!el) return

      const { zoom } = this.editor
      const centroX = el.offsetLeft + el.offsetWidth / 2
      const centroY = el.offsetTop + el.offsetHeight / 2
      this.aplicarTransformacao(
        (this.editor.precanvas.clientWidth / 2 - centroX) * zoom,
        (this.editor.precanvas.clientHeight / 2 - centroY) * zoom,
        zoom,
      )

      if (this.editor.node_selected) this.editor.node_selected.classList.remove('selected')
      this.editor.node_selected = el
      this.editor.first_click = el
      el.classList.add('selected')
      this.noSelecionadoId = meuId

      // remove e readiciona no proximo frame pra reiniciar a animacao se
      // clicar de novo no mesmo no
      el.classList.remove('fluxo-no-piscar')
      requestAnimationFrame(() => el.classList.add('fluxo-no-piscar'))
    },

    // Manda o recurso inteiro (nome/descricao/padrao/status), nao so
    // definicao - o FluxoRequest exige "nome" tanto no create quanto no
    // update (mesma convencao de toda a validation deste app), e so
    // reenviar o que ja veio do GET evita um PUT parcial invalido.
    async salvarRascunho() {
      this.sincronizarPosicoes()
      this.salvandoTexto = 'Salvando...'
      try {
        await window.axios.post(this.$store.state.api + 'fluxos/' + this.fluxo.id, {
          _method: 'PUT',
          nome: this.fluxo.nome,
          descricao: this.fluxo.descricao,
          padrao: this.fluxo.padrao,
          status: this.fluxo.status,
          definicao: this.fluxo.definicao,
        })
        this.salvandoTexto = 'Salvo'
        setTimeout(() => { this.salvandoTexto = '' }, 2000)
        return true
      } catch (error) {
        this.$error(error)
        this.salvandoTexto = ''
        return false
      }
    },

    async publicar() {
      this.validar()
      if (this.problemasValidacao.length) {
        this.$message(null, 'Resolva os problemas de validação antes de publicar', 'error')
        return
      }
      const salvou = await this.salvarRascunho()
      if (!salvou) {
        this.$message(null, 'Não foi possível salvar o rascunho - publicação cancelada', 'error')
        return
      }
      try {
        await window.axios.post(this.$store.state.api + 'fluxos/' + this.fluxo.id + '/publicar')
        this.$message('Sucesso', 'Fluxo publicado - atendimentos novos já usam essa versão', 'success')
      } catch (error) {
        this.$error(error)
      }
    },
  },
}
</script>

<style lang="scss">
.fluxo-editor-pagina {
  height: calc(100vh - 12rem);
  display: flex;
  flex-direction: column;
}

.fluxo-editor-corpo {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
}

.fluxo-editor-paleta {
  width: 260px;
  flex: 0 0 auto;
  overflow-y: auto;
  border-right: 1px solid #e4e4e4;
}

.fluxo-paleta-item {
  padding: 0.6rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}
.fluxo-paleta-item:hover {
  background: #f8f8f8;
}

.fluxo-editor-area {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  display: flex;
}

#fluxo-drawflow {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  background-size: 20px 20px;
  background-image: radial-gradient(circle, #ddd 1px, transparent 1px);
}

.fluxo-editor-inspetor {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  z-index: 10;
  border-left: 1px solid #e4e4e4;
  background: #fff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.08);
}

.fluxo-remover-conexao {
  position: absolute;
  transform: translate(-50%, -150%);
  z-index: 8;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.fluxo-editor-problemas {
  position: absolute;
  left: 10px;
  bottom: 10px;
  max-width: 480px;
  max-height: 40%;
  overflow-y: auto;
  z-index: 9;
}

#fluxo-drawflow .drawflow .drawflow-node.fluxo-no-pendente {
  border-color: #ff9f43;
  box-shadow: 0 0 0 3px rgba(255, 159, 67, 0.35);
}
#fluxo-drawflow .drawflow .drawflow-node.fluxo-no-pendente:not(.selected) {
  background: #fff3cd;
}
#fluxo-drawflow .drawflow .drawflow-node.fluxo-no-pendente::after {
  content: '⚠ ' attr(data-pendencias);
  position: absolute;
  top: -10px;
  right: -10px;
  padding: 0 0.4rem;
  border-radius: 10px;
  background: #ff9f43;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.4rem;
}
#fluxo-drawflow .drawflow .drawflow-node.fluxo-no-piscar {
  animation: fluxo-no-piscar 0.6s ease-in-out 3;
}
@keyframes fluxo-no-piscar {
  50% { box-shadow: 0 0 0 10px rgba(255, 159, 67, 0.6); }
}

.fluxo-problema-link {
  cursor: pointer;
  text-decoration: underline dotted;
}
.fluxo-problema-link:hover {
  text-decoration: underline;
}

.fluxo-no-titulo {
  font-weight: 600;
  padding: 0.4rem 0.6rem;
}
.fluxo-no-resumo {
  padding: 0 0.6rem 0.4rem;
  font-size: 0.75rem;
  color: #666;
  max-width: 180px;
}
</style>
